<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  formatDateTimeWithMilitarySupport,
  LuxonDateTimeFormats,
  type IAppointment,
  type IWarehouse
} from '@nova/core';

defineOptions({ name: 'AppointmentSummary' });

const props = defineProps<{
  appointment: IAppointment;
  warehouse: Partial<IWarehouse>;
}>();

const { t } = useI18n();

const isMilitaryTimeEnabled = computed(() => props.warehouse.settings?.enableMilitaryTime === true);
const timezone = computed(
  () => props.warehouse.timezone ?? props.appointment.dock?.warehouse?.timezone
);

function formatDateTime(
  dateTime: Date,
  format12h: LuxonDateTimeFormats,
  format24h: LuxonDateTimeFormats
): string {
  return formatDateTimeWithMilitarySupport(
    dateTime,
    timezone.value,
    format12h,
    isMilitaryTimeEnabled.value,
    format24h
  ).toUpperCase();
}

const appointmentDateTimeHeader = computed(() => {
  const date = formatDateTime(
    props.appointment.start,
    LuxonDateTimeFormats.MonthDayYearSlashed,
    LuxonDateTimeFormats.MonthDayYearSlashed
  );
  const startTime = formatDateTime(
    props.appointment.start,
    LuxonDateTimeFormats.Extended12HrTimeAMPM,
    LuxonDateTimeFormats.Extended24HrTime
  );
  const endTime = formatDateTime(
    props.appointment.end,
    LuxonDateTimeFormats.Extended12HrTimeAMPM,
    LuxonDateTimeFormats.Extended24HrTime
  );
  return t('appointmentSummary.dateTimeRange', { date, startTime, endTime });
});
</script>

<template>
  <div class="comet-i18n-exclude d-flex flex-column gap-2">
    <p class="typography-heading-sm-bold">
      {{ t('appointmentSummary.confirmationNumber', { number: appointment.confirmationNumber }) }}
    </p>
    <p class="typography-body-sm">{{ appointmentDateTimeHeader }}</p>
    <p class="typography-body-sm">{{ appointment.user?.company?.name }}</p>
  </div>
</template>
